import { USER_TYPES, encryptUserRole } from "./userTypeConstants";
import store from "../store";
import { resolveAdminLandingPage } from "./featureAccessHelpers";

// Re-export for existing imports from authHelper.
export { encryptUserRole, USER_TYPES } from "./userTypeConstants";

/**
 * Get initial link for user based on user type after login
 * @param String userType
 * @returns String
 */
export const getUserDashboardLink = (userType) => {
  switch (userType) {
    case USER_TYPES.admin:
      return getUserLandingPage();

    case USER_TYPES.customer:
      return "/customer/dashboard";

    case USER_TYPES.staff:
      return "/staff/dashboard";

    case USER_TYPES.superAdmin:
      return "/super-admin/dashboard";

    default:
      return getUserLandingPage();
  }
};

/**
 * Get user landing page
 */
export const getUserLandingPage = () => {
  try {
    const { featureAccess } = store.getState();
    if (featureAccess?.loaded) {
      return resolveAdminLandingPage(featureAccess.features);
    }
  } catch (error) {
    // fall through to storage-based preference
  }

  if (localStorage.page) {
    switch (localStorage.page) {
      case "stats":
        return "/admin/dashboard";
      case "scheduled-events":
        return "/admin/scheduled-events";
      default:
        return "/admin/dashboard";
    }
  }
  return "/admin/dashboard";
};

/**
 * Get List of user roles
 * @returns Mixed
 */
export const getUserRoles = () => {
  if (localStorage.config) {
    const config = JSON.parse(localStorage.config);
    return config.roles;
  }
  return null;
};

/**
 * Get list of admin businesses
 * @returns Mixed
 */
export const getUserBusinesses = () => {
  if (localStorage.config) {
    const config = JSON.parse(localStorage.config);
    return config.businesses;
  }
  return null;
};

/**
 * Get staff Business id
 * @returns mixed
 */
export const getStaffBusinessId = () => {
  if (localStorage.config) {
    const config = JSON.parse(localStorage.config);
    return config.staff_business_id;
  }
  return null;
};

/**
 * get business id
 * @return mixed
 */
export const getAdminBusinessId = () => {
  if (localStorage.businessId) {
    return localStorage.businessId;
  }
  return null;
};

/**
 * Business id for calendar list/config APIs.
 * Staff portal uses staff org business; admin portal uses admin business.
 */
export const getCalendarBusinessId = () => {
  if (
    typeof window !== "undefined" &&
    window.location?.pathname?.startsWith("/staff")
  ) {
    return getStaffBusinessId();
  }
  return getAdminBusinessId();
};

/**
 * get logged in user id
 * @return mixed
 */
export const getLoggedInUserId = () => {
  if (localStorage.config) {
    const config = JSON.parse(localStorage.config);
    return config?.user?.id;
  }
  return null;
};

/**
 * Whether the signed-in user's staff portal access is deactivated.
 */
export const isStaffPortalDeactivated = () => {
  if (!localStorage.config) {
    return false;
  }
  const config = JSON.parse(localStorage.config);
  const user = config?.user || {};
  return user.status === "D" || user.is_staff_acc_deleted === "E" || user.staff_org_locked === true;
};

/**
 * Skip plan feature fetch when a deactivated staff member only needs splash access.
 */
export const shouldSkipFeatureFetchOnLogin = () => {
  const roles = getUserRoles() || [];
  const staffOnly = roles.length === 1 && roles.includes("staff");
  return staffOnly && isStaffPortalDeactivated();
};

/**
 * After login, send deactivated staff to splash instead of auto-opening staff dashboard.
 */
export const shouldShowPortalSelectionAfterLogin = () => {
  const roles = getUserRoles() || [];
  const businesses = getUserBusinesses() || {};
  const businessIds = Object.values(businesses);

  if (isStaffPortalDeactivated() && roles.includes("staff")) {
    return true;
  }

  if (roles.length > 1) {
    return true;
  }

  if (roles.includes("admin") && businessIds.length > 1) {
    return true;
  }

  return !(roles.length === 1 && (businessIds.length === 0 || businessIds.length === 1));
};

/**
 * After a successful auth session is already in localStorage/Redux,
 * set portal context and navigate into the app (dashboard or portal picker).
 */
export const redirectIntoApp = (navigate) => {
  const userType = localStorage.userType;
  if (userType) {
    if (shouldShowPortalSelectionAfterLogin()) {
      localStorage.removeItem("userType");
      localStorage.removeItem("businessId");
      navigate("/admin/user-type");
    } else {
      navigate(getUserDashboardLink(userType));
    }
    return;
  }

  const userRoles = getUserRoles() || [];
  const userBusinesses = getUserBusinesses() || {};
  const businessIds = Object.values(userBusinesses);

  if (shouldShowPortalSelectionAfterLogin()) {
    localStorage.removeItem("businessId");
    navigate("/admin/user-type");
    return;
  }

  if (
    userRoles.length === 1 &&
    (businessIds.length === 0 || businessIds.length === 1)
  ) {
    localStorage.setItem("userType", encryptUserRole(userRoles[0]));
    if (localStorage.userType === USER_TYPES.admin && businessIds.length === 1) {
      localStorage.setItem("businessId", businessIds[0]);
    }
    navigate(getUserDashboardLink(localStorage.getItem("userType")));
    return;
  }

  localStorage.removeItem("businessId");
  navigate("/admin/user-type");
};

export const isStaffPortalRoleLocked = (role) => {
  return role === "staff" && isStaffPortalDeactivated();
};

export const STAFF_AVAILABILITY_LOCKED_MESSAGE =
  "Your availability updates are disabled. Please contact your administrator to enable them.";

export const canStaffUpdateAvailability = () => {
  if (!localStorage.config) {
    return false;
  }
  const config = JSON.parse(localStorage.config);
  const user = config?.user || {};
  if (
    user.update_schedule_status === undefined ||
    user.update_schedule_status === null
  ) {
    return false;
  }
  return user.update_schedule_status === "E";
};

export const syncLoggedInUserConfig = (userPatch) => {
  if (!localStorage.config || !userPatch) {
    return;
  }
  const config = JSON.parse(localStorage.config);
  config.user = { ...(config.user || {}), ...userPatch };
  localStorage.setItem("config", JSON.stringify(config));
};

/**
 * Refresh staff permission flags from the API (e.g. after admin changes settings).
 */
export const refreshStaffPortalPermissions = (onComplete) => {
  if (!localStorage.config || !localStorage.jwtToken) {
    if (onComplete) {
      onComplete();
    }
    return;
  }

  const roles = getUserRoles() || [];
  if (!roles.includes("staff")) {
    if (onComplete) {
      onComplete();
    }
    return;
  }

  import("../axios-instance").then(({ default: axios }) => {
    axios
      .get("user-details")
      .then((res) => {
        const profile = res?.data?.data ?? res?.data;
        if (profile && typeof profile === "object") {
          syncLoggedInUserConfig({
            status: profile.status,
            is_staff_acc_deleted: profile.is_staff_acc_deleted,
            update_schedule_status: profile.update_schedule_status,
            staff_org_locked: profile.staff_org_locked,
            stripe_status: profile.stripe_status,
            trial_ends_at: profile.trial_ends_at,
            trial_eligible: profile.trial_eligible,
          });
        }
      })
      .finally(() => {
        if (onComplete) {
          onComplete();
        }
      });
  });
};
